import type { Recipe } from "@/types/recipe"
import type { Review } from "@/types/review"

import { siteConfig } from "@/data/site"
import { absoluteUrl } from "@/lib/utils"

type BreadcrumbItem = {
  name: string
  item: string
}

type RatingSummary = {
  average: number
  count: number
}

const recipeCategoryLabels: Record<Recipe["category"], string> = {
  cucumbers: "Огірки",
  tomatoes: "Помідори",
  zucchini: "Кабачки",
  peppers: "Перець",
  eggplants: "Баклажани",
  berries: "Ягоди",
  jam: "Варення",
  compote: "Компоти",
  fermentation: "Ферментація",
  other: "Інше",
}

function isoDurationFromMinutes(totalMinutes: number) {
  if (totalMinutes <= 0) {
    return "PT0M"
  }

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0 && minutes > 0) {
    return `PT${hours}H${minutes}M`
  }

  if (hours > 0) {
    return `PT${hours}H`
  }

  return `PT${minutes}M`
}

function formatRecipeYield(servings: number) {
  if (servings === 1) {
    return "1 банка"
  }

  if (servings >= 2 && servings <= 4) {
    return `${servings} банки`
  }

  return `${servings} банок`
}

function getRecipeCategoryLabel(category: Recipe["category"]) {
  return recipeCategoryLabels[category]
}

function buildRecipeIngredientText(recipe: Recipe) {
  return recipe.ingredients.map((ingredient) =>
    ingredient.notes
      ? `${ingredient.name} — ${ingredient.amount} (${ingredient.notes})`
      : `${ingredient.name} — ${ingredient.amount}`
  )
}

function buildRecipeInstructions(recipe: Recipe) {
  return recipe.steps.map((step, index) => {
    const instructionParts = [step.description]

    if (typeof step.timerMinutes === "number") {
      instructionParts.push(`Орієнтовний час: ${step.timerMinutes} хв.`)
    }

    if (step.tip) {
      instructionParts.push(`Порада: ${step.tip}`)
    }

    return {
      "@type": "HowToStep",
      position: index + 1,
      name: step.title ?? `Крок ${index + 1}`,
      text: instructionParts.join(" "),
      url: `${absoluteUrl(`/recipes/${recipe.slug}`, siteConfig.url)}#step-${index + 1}`,
    }
  })
}

function buildReviewSchema(reviews: Review[]) {
  return reviews.slice(0, 5).map((review) => ({
    "@type": "Review",
    author: {
      "@type": "Person",
      name: review.authorName,
    },
    reviewRating: {
      "@type": "Rating",
      ratingValue: review.rating,
      bestRating: 5,
      worstRating: 1,
    },
    reviewBody: review.text,
    datePublished: review.createdAt,
  }))
}

export function buildBreadcrumbSchema(items: BreadcrumbItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: item.item,
    })),
  }
}

export function buildRecipeSchema(params: {
  recipe: Recipe
  ratingSummary: RatingSummary
  reviews: Review[]
}) {
  const { recipe, ratingSummary, reviews } = params

  const recipeUrl = absoluteUrl(`/recipes/${recipe.slug}`, siteConfig.url)
  const imageUrl = absoluteUrl(recipe.image, siteConfig.url)

  const schema: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Recipe",
    name: recipe.title,
    description: recipe.description,
    image: [imageUrl],
    url: recipeUrl,
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": recipeUrl,
    },
    author: {
      "@type": "Person",
      name: recipe.authorName ?? siteConfig.mascot.name,
    },
    publisher: {
      "@type": "Organization",
      name: siteConfig.name,
      url: siteConfig.url,
    },
    datePublished: recipe.publishedAt,
    dateModified: recipe.updatedAt ?? recipe.publishedAt,
    recipeCategory: getRecipeCategoryLabel(recipe.category),
    keywords: recipe.tags.join(", "),
    recipeYield: formatRecipeYield(recipe.servings),
    prepTime: isoDurationFromMinutes(recipe.prepTimeMinutes),
    cookTime: isoDurationFromMinutes(recipe.cookTimeMinutes),
    totalTime: isoDurationFromMinutes(recipe.totalTimeMinutes),
    recipeIngredient: buildRecipeIngredientText(recipe),
    recipeInstructions: buildRecipeInstructions(recipe),
  }

  if (ratingSummary.count > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Number(ratingSummary.average.toFixed(1)),
      reviewCount: ratingSummary.count,
      bestRating: 5,
      worstRating: 1,
    }
  }

  if (reviews.length > 0) {
    schema.review = buildReviewSchema(reviews)
  }

  return schema
}